import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LAST_UPDATED, SITE_NAME, TOS_SECTIONS, TOS_STORAGE_KEY } from "@/lib/legal";
import { ShieldAlert } from "lucide-react";

/**
 * Blocking modal — cannot be closed until the visitor accepts.
 * Once accepted, we write TOS_STORAGE_KEY to localStorage so we don't ask again
 * (until the TOS_VERSION is bumped, which invalidates the old key).
 */
export default function TosModal() {
    const [visible, setVisible] = useState(false);
    const [agreed, setAgreed] = useState(false);

    useEffect(() => {
        try {
            const accepted = localStorage.getItem(TOS_STORAGE_KEY);
            if (!accepted) setVisible(true);
        } catch {
            // localStorage unavailable (private mode) — always show the modal
            setVisible(true);
        }
    }, []);

    // Lock body scroll while the gate is open
    useEffect(() => {
        if (!visible) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [visible]);

    if (!visible) return null;

    const accept = () => {
        try {
            localStorage.setItem(TOS_STORAGE_KEY, new Date().toISOString());
        } catch {
            // ignore storage errors — user can re-accept next visit
        }
        setVisible(false);
    };

    return (
        <div
            data-testid="tos-modal-overlay"
            className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tos-modal-title"
        >
            <div
                data-testid="tos-modal"
                className="w-full max-w-2xl bg-[#0f0f0f] border border-[#2a2a2a] shadow-[0_25px_60px_-15px_rgba(255,69,0,0.35)] max-h-[92vh] flex flex-col"
            >
                {/* Header */}
                <div className="border-b border-[#2a2a2a] px-6 py-4 flex items-center gap-3">
                    <div className="h-9 w-9 bg-[#ff4500] flex items-center justify-center shrink-0">
                        <ShieldAlert className="h-5 w-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500]">Before you continue</div>
                        <h2 id="tos-modal-title" className="font-display text-2xl tracking-wide leading-none mt-1">
                            Terms of Service &amp; Privacy Policy
                        </h2>
                    </div>
                </div>

                {/* Scrollable body */}
                <div
                    data-testid="tos-modal-body"
                    className="flex-1 overflow-y-auto px-6 py-5 text-sm text-[#cfcfcf] leading-relaxed space-y-6"
                    style={{ scrollbarWidth: "thin" }}
                >
                    <div className="border border-[#ff4500]/40 bg-[#ff4500]/10 px-4 py-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">Important disclaimer</div>
                        <p className="text-[13px] text-white leading-relaxed">
                            <strong>{SITE_NAME}</strong> is an independent directory and is <strong>NOT affiliated with,
                            endorsed by, or sponsored by</strong> any of the organizations, leagues, sanctioning bodies,
                            teams, trainers, events, or brands listed or referenced on this site. All trademarks belong
                            to their respective owners.
                        </p>
                    </div>

                    <p>
                        By clicking "I Agree" you accept our Terms of Service and Privacy Policy. Please read the
                        highlights below. The full text is available at{" "}
                        <Link to="/terms" className="text-[#ff4500] hover:underline">/terms</Link> and{" "}
                        <Link to="/privacy" className="text-[#ff4500] hover:underline">/privacy</Link>.
                    </p>

                    {TOS_SECTIONS.slice(0, 6).map((sec) => (
                        <section key={sec.heading}>
                            <h3 className="font-display text-base tracking-wide text-white mb-1">{sec.heading}</h3>
                            {sec.paragraphs.map((p, i) => (
                                <p key={i} className="mb-2 text-[13px]">{p}</p>
                            ))}
                        </section>
                    ))}

                    <div className="border-t border-[#2a2a2a] pt-4 text-xs text-[#a0a0a0]">
                        For the full 12-section Terms of Service (including limitation of liability, indemnification,
                        and dispute resolution) see <Link to="/terms" className="text-[#ff4500] hover:underline">the Terms page</Link>.
                        For the full Privacy Policy see <Link to="/privacy" className="text-[#ff4500] hover:underline">the Privacy page</Link>.
                    </div>

                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#a0a0a0]">Last updated: {LAST_UPDATED}</div>
                </div>

                {/* Footer / accept */}
                <div className="border-t border-[#2a2a2a] px-6 py-4 space-y-3 bg-[#0a0a0a]">
                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="tos-agree-label">
                        <Checkbox
                            checked={agreed}
                            onCheckedChange={(v) => setAgreed(v === true)}
                            data-testid="tos-agree-checkbox"
                            className="mt-0.5 h-5 w-5 rounded-none border-[#3a3a3a] data-[state=checked]:bg-[#ff4500] data-[state=checked]:border-[#ff4500]"
                        />
                        <span className="text-sm text-white leading-tight">
                            I have read and agree to the{" "}
                            <Link to="/terms" className="text-[#ff4500] hover:underline">Terms of Service</Link>{" "}
                            and{" "}
                            <Link to="/privacy" className="text-[#ff4500] hover:underline">Privacy Policy</Link>,
                            and I acknowledge that {SITE_NAME} is not affiliated with any of the organizations, teams, trainers, or events listed on this site, and that {SITE_NAME} is not responsible for, and does not endorse, the accuracy, completeness, legality, safety, or usefulness of any Added Content.
                        </span>
                    </label>
                    <Button
                        data-testid="tos-accept-btn"
                        disabled={!agreed}
                        onClick={accept}
                        className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {agreed ? "I Agree — Continue" : "Please Check the Box to Continue"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
